import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  Shield, 
  Info, 
  XCircle, 
  CheckCircle, 
  Clock,
  Calendar,
  Scale
} from 'lucide-react';

const DisclaimerPage = () => {
  const lastUpdated = "December 1, 2024";

  const disclaimers = [
    {
      title: "Service Availability",
      icon: <Clock className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "No Guaranteed Uptime",
          description: "While we strive to maintain high service availability, we do not guarantee 100% uptime. The platform may be temporarily unavailable due to maintenance, technical issues, or other factors beyond our control."
        },
        {
          subtitle: "Maintenance Windows",
          description: "We may perform scheduled maintenance that affects service availability. We will attempt to provide advance notice when possible, but emergency maintenance may occur without prior notification."
        }
      ]
    },
    {
      title: "Data Accuracy",
      icon: <CheckCircle className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "User-Provided Data",
          description: "Cricket statistics, scores, and match data are primarily user-generated. While we provide tools to ensure accuracy, we cannot guarantee that all data entered by users is completely accurate or up-to-date."
        },
        {
          subtitle: "Verification Responsibility",
          description: "Users are responsible for verifying the accuracy of cricket data they input. We recommend cross-checking important statistics and scores with official sources when possible."
        }
      ]
    },
    {
      title: "Technical Limitations",
      icon: <XCircle className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Internet Connectivity",
          description: "The platform requires internet connectivity to function. We are not responsible for issues arising from poor internet connections, network outages, or other connectivity problems."
        },
        {
          subtitle: "Device Compatibility",
          description: "While we strive for broad compatibility, the platform may not work optimally on all devices or browsers. Users should ensure their devices meet minimum system requirements."
        }
      ]
    },
    {
      title: "Third-Party Services",
      icon: <Info className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "External Dependencies",
          description: "Our platform may rely on third-party services, APIs, or integrations. We are not responsible for the availability, accuracy, or functionality of these external services."
        },
        {
          subtitle: "Third-Party Terms",
          description: "Use of third-party services is subject to their respective terms of service and privacy policies, which are separate from our own terms and policies."
        }
      ]
    },
    {
      title: "Legal and Regulatory",
      icon: <Scale className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Compliance",
          description: "Users are responsible for ensuring their use of the platform complies with applicable laws, regulations, and cricket governing body rules in their jurisdiction."
        },
        {
          subtitle: "Official Records",
          description: "Our platform is designed for convenience and record-keeping but may not be recognized as official cricket records by all governing bodies or organizations."
        }
      ]
    },
    {
      title: "Financial and Business",
      icon: <Shield className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "No Financial Advice",
          description: "We do not provide financial, investment, or business advice. Any decisions made based on data from our platform are made at your own risk."
        },
        {
          subtitle: "Commercial Use",
          description: "Commercial use of our platform may require additional licensing or agreements. Please contact us for commercial licensing information."
        }
      ]
    }
  ];

  const limitations = [
    {
      category: "Liability Limitations",
      items: [
        "We are not liable for any indirect, incidental, special, or consequential damages",
        "Our total liability is limited to the amount paid for our services in the 12 months preceding the claim",
        "We are not responsible for data loss, corruption, or unauthorized access",
        "No warranty is provided for fitness for a particular purpose"
      ]
    },
    {
      category: "User Responsibilities",
      items: [
        "Users must maintain the security of their account credentials",
        "Users are responsible for backing up their important data",
        "Users must comply with all applicable laws and regulations",
        "Users should report security vulnerabilities or issues promptly"
      ]
    },
    {
      category: "Data and Privacy",
      items: [
        "We implement reasonable security measures but cannot guarantee absolute security",
        "Data may be stored in multiple locations for redundancy and performance",
        "We may retain data for legal, regulatory, or business purposes",
        "Users should review our Privacy Policy for complete information"
      ]
    }
  ];

  const importantNotes = [
    "This disclaimer is part of our Terms of Service and Privacy Policy",
    "Continued use of our platform constitutes acceptance of these disclaimers",
    "We may update these disclaimers from time to time",
    "For questions about these disclaimers, please contact our legal team"
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Disclaimer
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            Important information about the limitations and disclaimers for our cricket scoring platform.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Badge variant="outline">
              <Calendar className="h-3 w-3 mr-1" />
              Last Updated: {lastUpdated}
            </Badge>
            <Badge variant="destructive">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Important Notice
            </Badge>
          </div>
        </div>

        {/* Warning Notice */}
        <Card className="mb-8 bg-destructive/10 border-destructive/20">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-destructive mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Important Notice</h2>
                <p className="text-muted-foreground">
                  Please read this disclaimer carefully. By using our cricket scoring platform, you acknowledge and agree to the limitations and disclaimers outlined below. This information is essential for understanding the scope and limitations of our services.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Disclaimers */}
        <div className="space-y-8">
          {disclaimers.map((disclaimer, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  {disclaimer.icon}
                  <CardTitle className="text-xl">{disclaimer.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {disclaimer.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="space-y-2">
                      <h3 className="font-semibold text-foreground">{item.subtitle}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Limitations Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">Additional Limitations</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {limitations.map((limitation, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{limitation.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {limitation.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2 text-sm text-muted-foreground">
                        <AlertTriangle className="h-3 w-3 text-warning mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Specific Disclaimers */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Info className="h-5 w-5 text-primary" />
              <span>Specific Disclaimers</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-warning/5">
                <h3 className="font-semibold text-foreground mb-2">Cricket Statistics and Records</h3>
                <p className="text-sm text-muted-foreground">
                  While we strive for accuracy in cricket statistics and records, our platform should not be considered the sole authoritative source for official cricket records. Users should verify important statistics with official cricket boards and governing bodies.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg bg-warning/5">
                <h3 className="font-semibold text-foreground mb-2">Live Scoring Accuracy</h3>
                <p className="text-sm text-muted-foreground">
                  Live scoring features depend on real-time data input by users. Delays, errors, or technical issues may affect the accuracy of live scores. We recommend cross-referencing with official sources for critical match information.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg bg-warning/5">
                <h3 className="font-semibold text-foreground mb-2">Tournament Management</h3>
                <p className="text-sm text-muted-foreground">
                  Our tournament management features are designed to assist organizers but do not replace official tournament administration. Users are responsible for ensuring compliance with cricket governing body regulations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Card className="mt-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold text-foreground mb-4 text-center">Important Notes</h2>
            <div className="space-y-2">
              {importantNotes.map((note, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>{note}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mt-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">Questions About This Disclaimer?</h2>
            <p className="text-muted-foreground mb-4">
              If you have questions about these disclaimers or need clarification, please contact us:
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-foreground">
                <strong>Email:</strong> legal@cricketscoring.com
              </p>
              <p className="text-foreground">
                <strong>Address:</strong> 123 Cricket Lane, Sports City, SC 12345
              </p>
              <p className="text-foreground">
                <strong>Phone:</strong> +1 (555) 123-4567
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Final Notice */}
        <Card className="mt-8 bg-destructive/5 border-destructive/20">
          <CardContent className="pt-6 text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <h3 className="font-semibold text-foreground">Final Notice</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              By using our cricket scoring platform, you acknowledge that you have read, understood, and agree to all disclaimers, limitations, and terms outlined in this document and our other legal documents.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DisclaimerPage; 